import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Polygon, Polyline, Marker, InfoWindow } from '@react-google-maps/api';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import GoogleMapProvider from '../../components/map/GoogleMapProvider';
import BaseMap, { MapPosition } from '../../components/map/BaseMap';
import { useProject } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import { mapToolsService } from '../../services/mapTools.service';
import { addressDatabaseService } from '../../services/addressDatabase.service';
import { workloadService } from '../../services/workload.service';
import { batchCreateAddresses } from '../../services/address.service';
import { MapArea, MapPin, PinCategory, PIN_CATEGORIES, pinCategoryMeta } from '../../types/maps.types';
import { areaIntelligenceService, AreaProfile } from '../../services/areaIntelligence.service';
import { Pentagon, MapPin as MapPinIcon, Trash2, Save, X, Eye, EyeOff, Footprints, Loader2, School, BarChart3 } from 'lucide-react';
import { toast } from 'react-hot-toast';

type Mode = 'none' | 'draw' | 'pin';
interface LeadHome { id: string; name: string; lat: number; lng: number }

// Distinct colors so multiple areas are easy to tell apart.
const AREA_COLORS = ['#4F46E5', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#BE185D', '#65A30D'];
const areaColorAt = (i: number) => AREA_COLORS[i % AREA_COLORS.length];

// Position for an area's name label — biased into the top-left of its bbox.
const labelPos = (paths: { lat: number; lng: number }[]) => {
  const lats = paths.map(p => p.lat), lngs = paths.map(p => p.lng);
  const north = Math.max(...lats), south = Math.min(...lats), west = Math.min(...lngs), east = Math.max(...lngs);
  return { lat: north - (north - south) * 0.06, lng: west + (east - west) * 0.10 };
};
const areaLabel = (name: string) => ({
  clickable: false,
  label: { text: name, color: 'rgba(31,41,55,0.8)', fontSize: '13px', fontWeight: '700' } as any,
  icon: { path: 0 /* CIRCLE */, scale: 0, fillOpacity: 0, strokeOpacity: 0 } as any,
});

// Ray-casting point-in-polygon for counting leads inside an area.
const pointInPolygon = (pt: { lat: number; lng: number }, paths: { lat: number; lng: number }[]): boolean => {
  let inside = false;
  for (let i = 0, j = paths.length - 1; i < paths.length; j = i++) {
    const xi = paths[i].lng, yi = paths[i].lat;
    const xj = paths[j].lng, yj = paths[j].lat;
    const intersect = ((yi > pt.lat) !== (yj > pt.lat)) &&
      (pt.lng < ((xj - xi) * (pt.lat - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

const MapsInner: React.FC = () => {
  const { selectedProjectId, selectedProject } = useProject();
  const { currentUser, userProfile } = useAuth();

  const [areas, setAreas] = useState<MapArea[]>([]);
  const [pins, setPins] = useState<MapPin[]>([]);
  const [leadHomes, setLeadHomes] = useState<LeadHome[]>([]);
  const [showLeadHomes, setShowLeadHomes] = useState(false);
  const [visibleCampusIds, setVisibleCampusIds] = useState<Set<string>>(new Set());
  const [campusMarkers, setCampusMarkers] = useState<{ name: string; lat: number; lng: number }[]>([]);
  // Stable map center (a fresh object each render made GoogleMap snap back).
  const [mapCenter, setMapCenter] = useState<MapPosition>({ lat: 39.8283, lng: -98.5795 });

  const [mode, setMode] = useState<Mode>('none');
  const [pinCategory, setPinCategory] = useState<PinCategory>('bus_stop');
  const [drawingVertices, setDrawingVertices] = useState<MapPosition[]>([]);

  // Pending (unsaved) draw / pin awaiting a name+notes.
  const [pendingPaths, setPendingPaths] = useState<{ lat: number; lng: number }[] | null>(null);
  const [pendingPin, setPendingPin] = useState<MapPosition | null>(null);
  const [form, setForm] = useState({ name: '', notes: '' });

  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  // Area intelligence (Census/ACS) report
  const [reportArea, setReportArea] = useState<MapArea | null>(null);
  const [reportData, setReportData] = useState<AreaProfile | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);
  const navigate = useNavigate();

  const segAddrString = (sa: any) => sa ? [sa.address, sa.city, sa.state, sa.zipCode].filter(Boolean).join(', ') : '';

  // Default the map to the project's school location (geocode the address if no
  // coordinates are stored). Set once per project so it doesn't snap back.
  useEffect(() => {
    let active = true;
    const sa = selectedProject?.schoolAddress;
    if (sa?.location?.lat) { setMapCenter({ lat: sa.location.lat, lng: sa.location.lng }); return; }
    const addr = segAddrString(sa) || segAddrString(selectedProject?.schoolSegments?.[0]?.schoolAddress);
    if (!addr || !(window as any).google) return;
    new google.maps.Geocoder().geocode({ address: addr }).then(r => {
      const g = r.results?.[0]?.geometry?.location;
      if (active && g) setMapCenter({ lat: g.lat(), lng: g.lng() });
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  // Geocode each campus (segment) address so they can be toggled individually.
  useEffect(() => {
    if (!(window as any).google) return;
    let active = true;
    setVisibleCampusIds(new Set());
    const segs = selectedProject?.schoolSegments || [];
    const items = segs.length > 0
      ? segs.map(s => ({ name: s.name, sa: s.schoolAddress }))
      : (selectedProject?.schoolAddress ? [{ name: selectedProject?.name || 'Campus', sa: selectedProject.schoolAddress }] : []);
    const geocoder = new google.maps.Geocoder();
    (async () => {
      const out: { name: string; lat: number; lng: number }[] = [];
      for (const it of items) {
        const loc = (it.sa as any)?.location;
        if (loc?.lat) { out.push({ name: it.name, lat: loc.lat, lng: loc.lng }); continue; }
        const addr = segAddrString(it.sa);
        if (!addr) continue;
        try {
          const r = await geocoder.geocode({ address: addr });
          const g = r.results?.[0]?.geometry?.location;
          if (g) out.push({ name: it.name, lat: g.lat(), lng: g.lng() });
        } catch { /* skip */ }
      }
      if (active) setCampusMarkers(out);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  // #9 — turn a saved area into an every-house canvassing workload.
  const generateWalkList = async (area: MapArea) => {
    if (!selectedProjectId || !currentUser || generating) return;
    setGenerating('Finding buildings…');
    try {
      const stops = await addressDatabaseService.getEveryBuildingInPolygon(area.paths, (m) => setGenerating(m));
      if (stops.length === 0) { toast.error('No buildings found in this area'); setGenerating(null); return; }

      const lats = area.paths.map(p => p.lat), lngs = area.paths.map(p => p.lng);
      const bounds = { north: Math.max(...lats), south: Math.min(...lats), east: Math.max(...lngs), west: Math.min(...lngs) };
      const centroid = { lat: lats.reduce((a, b) => a + b, 0) / lats.length, lng: lngs.reduce((a, b) => a + b, 0) / lngs.length };

      setGenerating(`Creating workload (${stops.length} stops)…`);
      const workload = await workloadService.create({
        name: `${area.name} — Walk List`,
        projectId: selectedProjectId,
        type: 'saturation',
        territory: { name: area.name, type: 'polygon', polygon: { coordinates: area.paths }, centerPoint: centroid, bounds } as any,
        sourceMetadata: { type: 'saturation', originPoint: centroid, generatedAt: Timestamp.now(), addressSource: 'geocoding_api' } as any,
      } as any, currentUser.uid, userProfile?.name || userProfile?.email || 'User');

      const fCity = selectedProject?.schoolAddress?.city || '';
      const fState = selectedProject?.schoolAddress?.state || '';
      const fZip = selectedProject?.schoolAddress?.zipCode || '';
      const dtos = stops.map(s => ({
        street: s.addressed ? `${s.houseNumber} ${s.street}`.trim() : 'Unmapped building',
        city: s.city || fCity,
        state: s.state || fState,
        zipCode: s.zipCode || fZip,
        location: { lat: s.lat, lng: s.lng },
      }));

      const res = await batchCreateAddresses(workload.id, dtos as any, (done, total) => setGenerating(`Saving stops ${done}/${total}…`));
      const addressed = stops.filter(s => s.addressed).length;
      toast.success(`Walk list created: ${res.created} stops (${addressed} addressed, ${res.created - addressed} pin-only). Assign it in Workload Management.`);
      setGenerating(null);
      setSelectedAreaId(null);
      navigate('/workloads/manage');
    } catch (e) {
      console.error('Generate walk list failed:', e);
      toast.error('Failed to generate walk list');
      setGenerating(null);
    }
  };

  const load = useCallback(async () => {
    if (!selectedProjectId) { setAreas([]); setPins([]); return; }
    const [a, p] = await Promise.all([
      mapToolsService.getAreas(selectedProjectId),
      mapToolsService.getPins(selectedProjectId),
    ]);
    setAreas(a);
    setPins(p);
  }, [selectedProjectId]);

  useEffect(() => { load(); }, [load]);

  // Lazily load lead homes (those with GPS) the first time the overlay is on.
  useEffect(() => {
    const run = async () => {
      if (!showLeadHomes || !selectedProjectId || leadHomes.length > 0) return;
      const snap = await getDocs(query(collection(db, 'leads'), where('projectId', '==', selectedProjectId)));
      const homes: LeadHome[] = [];
      snap.docs.forEach(d => {
        const l = d.data() as any;
        const lat = l.gpsLocation?.latitude, lng = l.gpsLocation?.longitude;
        if (typeof lat === 'number' && typeof lng === 'number') {
          homes.push({ id: d.id, name: l.parentName || 'Lead', lat, lng });
        }
      });
      setLeadHomes(homes);
    };
    run();
  }, [showLeadHomes, selectedProjectId, leadHomes.length]);

  // Click-to-draw polygon — Google removed the DrawingManager API in Maps
  // JS v3.65, so we build the polygon from tapped vertices ourselves.
  const handleMapClick = useCallback((pos: MapPosition) => {
    if (mode === 'draw') { setDrawingVertices(v => [...v, pos]); return; }
    if (mode === 'pin') {
      // Stay in pin mode so you can drop a series of pins (name+save each).
      setPendingPin(pos);
      setForm({ name: '', notes: '' });
    }
  }, [mode]);

  const finishDrawing = () => {
    if (drawingVertices.length < 3) { toast.error('Tap at least 3 points to make an area'); return; }
    setPendingPaths(drawingVertices);
    setForm({ name: '', notes: '' });
    setDrawingVertices([]);
    setMode('none');
  };

  const cancelDrawing = () => { setDrawingVertices([]); setMode('none'); };

  const saveArea = async () => {
    if (!pendingPaths || !selectedProjectId || !currentUser) return;
    if (!form.name.trim()) { toast.error('Name the area'); return; }
    try {
      await mapToolsService.createArea({
        projectId: selectedProjectId,
        name: form.name.trim(),
        notes: form.notes.trim() || undefined as any,
        paths: pendingPaths,
        color: areaColorAt(areas.length),
        createdBy: currentUser.uid,
        createdByName: userProfile?.name || userProfile?.email || 'User',
      } as any);
      toast.success('Area saved');
      setPendingPaths(null);
      await load();
    } catch (e) { console.error(e); toast.error('Failed to save area'); }
  };

  const savePin = async () => {
    if (!pendingPin || !selectedProjectId || !currentUser) return;
    try {
      await mapToolsService.createPin({
        projectId: selectedProjectId,
        category: pinCategory,
        name: form.name.trim() || undefined as any,
        notes: form.notes.trim() || undefined as any,
        location: { lat: pendingPin.lat, lng: pendingPin.lng },
        createdBy: currentUser.uid,
        createdByName: userProfile?.name || userProfile?.email || 'User',
      } as any);
      toast.success('Pin added');
      setPendingPin(null);
      await load();
    } catch (e) { console.error(e); toast.error('Failed to add pin'); }
  };

  const leadsInArea = (area: MapArea): number =>
    leadHomes.filter(h => pointInPolygon({ lat: h.lat, lng: h.lng }, area.paths)).length;

  const focusArea = (area: MapArea) => {
    if (!mapRef || !area.paths.length || !(window as any).google) return;
    const bounds = new google.maps.LatLngBounds();
    area.paths.forEach(p => bounds.extend(p));
    mapRef.fitBounds(bounds);
    setSelectedAreaId(area.id);
  };

  const selectedArea = areas.find(a => a.id === selectedAreaId) || null;
  const selectedPin = pins.find(p => p.id === selectedPinId) || null;
  const pinIcon = (color: string) => ((window as any).google ? {
    path: google.maps.SymbolPath.CIRCLE, fillColor: color, fillOpacity: 1,
    strokeColor: '#ffffff', strokeWeight: 2, scale: 8,
  } : undefined);
  // Render a typed pin as its emoji glyph (invisible marker shape + emoji label).
  const emojiMarker = (emoji: string) => ({
    label: { text: emoji, fontSize: '22px' } as any,
    icon: ((window as any).google ? { path: google.maps.SymbolPath.CIRCLE, scale: 0, fillOpacity: 0, strokeOpacity: 0 } : undefined) as any,
  });

  if (!selectedProjectId) {
    return <div className="p-6 text-gray-500">Select a project to use the maps tool.</div>;
  }

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Side panel */}
      <div className="w-72 border-r bg-white overflow-y-auto flex-shrink-0">
        <div className="p-4 border-b">
          <h1 className="text-lg font-semibold text-gray-900">Maps</h1>
          <p className="text-xs text-gray-500">{selectedProject?.name}</p>
        </div>

        {/* Tools */}
        <div className="p-4 border-b space-y-2">
          <div>
            <button
              onClick={() => { if (mode === 'draw') { cancelDrawing(); } else { setMode('draw'); setPendingPin(null); setDrawingVertices([]); } }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${mode === 'draw' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <Pentagon className="w-4 h-4" /> {mode === 'draw' ? `Drawing… (${drawingVertices.length} pts)` : 'Draw area'}
            </button>
            {mode === 'draw' && (
              <div className="mt-2 space-y-1.5">
                <p className="text-xs text-gray-500">Tap the map to add corners, then Finish.</p>
                <div className="flex gap-1.5">
                  <button onClick={finishDrawing} disabled={drawingVertices.length < 3} className="flex-1 px-2 py-1.5 bg-emerald-600 text-white rounded text-xs font-medium disabled:opacity-50">Finish</button>
                  <button onClick={() => setDrawingVertices(v => v.slice(0, -1))} disabled={!drawingVertices.length} className="px-2 py-1.5 bg-gray-100 text-gray-700 rounded text-xs disabled:opacity-50">Undo</button>
                  <button onClick={cancelDrawing} className="px-2 py-1.5 bg-gray-100 text-gray-700 rounded text-xs">Cancel</button>
                </div>
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => { setMode(mode === 'pin' ? 'none' : 'pin'); setPendingPaths(null); }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${mode === 'pin' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <MapPinIcon className="w-4 h-4" /> {mode === 'pin' ? 'Click map to drop pin…' : 'Add pin'}
            </button>
            {mode === 'pin' && (
              <select
                value={pinCategory}
                onChange={(e) => setPinCategory(e.target.value as PinCategory)}
                className="mt-2 w-full px-2 py-1.5 border rounded-md text-sm"
              >
                {PIN_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            )}
          </div>
          <button
            onClick={() => setShowLeadHomes(v => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            {showLeadHomes ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showLeadHomes ? 'Hide parent homes' : 'Show parent homes'}
          </button>
          {campusMarkers.length > 0 && (
            <div className="pt-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                  <School className="w-3.5 h-3.5" /> Campuses
                </span>
                <button
                  onClick={() => setVisibleCampusIds(new Set(visibleCampusIds.size === campusMarkers.length ? [] : campusMarkers.map(c => c.name)))}
                  className="text-xs text-indigo-600"
                >{visibleCampusIds.size === campusMarkers.length ? 'Hide all' : 'Show all'}</button>
              </div>
              {campusMarkers.map(c => (
                <label key={c.name} className="flex items-center gap-2 py-0.5 text-sm cursor-pointer text-gray-700">
                  <input
                    type="checkbox"
                    checked={visibleCampusIds.has(c.name)}
                    onChange={(e) => setVisibleCampusIds(prev => {
                      const n = new Set(prev);
                      if (e.target.checked) { n.add(c.name); if (mapRef) mapRef.panTo({ lat: c.lat, lng: c.lng }); }
                      else n.delete(c.name);
                      return n;
                    })}
                  />
                  <span className="truncate">{c.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Saved areas */}
        <div className="p-4 border-b">
          <h2 className="text-xs font-semibold text-gray-500 uppercase mb-2">Areas ({areas.length})</h2>
          {areas.length === 0 ? (
            <p className="text-xs text-gray-400">Draw an area to save it here.</p>
          ) : (
            <ul className="space-y-1">
              {areas.map((a, i) => (
                <li key={a.id}>
                  <button onClick={() => focusArea(a)} className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 flex items-center gap-2 ${selectedAreaId === a.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'}`}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: areaColorAt(i) }} />
                    <span className="flex-1 truncate">{a.name}{showLeadHomes && <span className="text-xs text-gray-400"> · {leadsInArea(a)} homes</span>}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pins */}
        <div className="p-4">
          <h2 className="text-xs font-semibold text-gray-500 uppercase mb-2">Pins ({pins.length})</h2>
          {pins.length === 0 ? (
            <p className="text-xs text-gray-400">Add typed pins (bus stops, no-go areas…).</p>
          ) : (
            <ul className="space-y-1">
              {pins.map(p => {
                const meta = pinCategoryMeta(p.category);
                return (
                  <li key={p.id}>
                    <button onClick={() => { setSelectedPinId(p.id); if (mapRef) mapRef.panTo(p.location); }} className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 text-gray-700 flex items-center gap-2">
                      <span className="flex-shrink-0">{meta.emoji}</span>
                      {p.name || meta.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        {generating && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-gray-900 text-white text-sm px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> {generating}
          </div>
        )}
        <BaseMap center={mapCenter} zoom={12} onClick={handleMapClick} onMapLoad={setMapRef}>
          {/* In-progress drawing (click-to-place vertices) */}
          {mode === 'draw' && drawingVertices.length > 0 && (
            <>
              {drawingVertices.length >= 3 ? (
                <Polygon paths={drawingVertices} options={{ fillColor: '#059669', fillOpacity: 0.15, strokeColor: '#059669', strokeWeight: 2, clickable: false }} />
              ) : (
                <Polyline path={drawingVertices} options={{ strokeColor: '#059669', strokeWeight: 2 }} />
              )}
              {drawingVertices.map((v, i) => (
                <Marker key={i} position={v} icon={pinIcon('#059669')} />
              ))}
            </>
          )}

          {/* Saved + pending areas */}
          {areas.map((a, i) => {
            const ac = areaColorAt(i);
            return (
              <React.Fragment key={a.id}>
                <Polygon
                  paths={a.paths}
                  options={{
                    fillColor: ac, fillOpacity: selectedAreaId === a.id ? 0.3 : 0.15,
                    strokeColor: ac, strokeWeight: selectedAreaId === a.id ? 3 : 2,
                    // Let clicks pass through while drawing/dropping pins, so you
                    // can place pins inside an existing area.
                    clickable: mode === 'none',
                  }}
                  onClick={() => setSelectedAreaId(a.id)}
                />
                <Marker position={labelPos(a.paths)} {...areaLabel(a.name)} />
              </React.Fragment>
            );
          })}
          {pendingPaths && (
            <Polygon paths={pendingPaths} options={{ fillColor: '#059669', fillOpacity: 0.25, strokeColor: '#059669', strokeWeight: 2 }} />
          )}

          {/* Pins (rendered as their category emoji) */}
          {pins.map(p => (
            <Marker key={p.id} position={p.location} {...emojiMarker(pinCategoryMeta(p.category).emoji)} onClick={() => setSelectedPinId(p.id)} />
          ))}
          {pendingPin && <Marker position={pendingPin} {...emojiMarker(pinCategoryMeta(pinCategory).emoji)} />}

          {/* Lead homes */}
          {showLeadHomes && leadHomes.map(h => (
            <Marker key={h.id} position={{ lat: h.lat, lng: h.lng }} icon={pinIcon('#9CA3AF')} title={h.name} />
          ))}

          {/* Campuses (toggled individually) */}
          {campusMarkers.filter(c => visibleCampusIds.has(c.name)).map((c, i) => (
            <Marker key={`campus-${c.name}-${i}`} position={{ lat: c.lat, lng: c.lng }} title={`Campus: ${c.name}`} />
          ))}

          {/* Area info window */}
          {selectedArea && selectedArea.paths[0] && (
            <InfoWindow position={selectedArea.paths[0]} onCloseClick={() => setSelectedAreaId(null)}>
              <div className="max-w-[220px]">
                <div className="font-semibold text-gray-900">{selectedArea.name}</div>
                {selectedArea.notes && <div className="text-sm text-gray-600 mt-1">{selectedArea.notes}</div>}
                {showLeadHomes && <div className="text-xs text-gray-500 mt-1">{leadsInArea(selectedArea)} parent home(s) inside</div>}
                <button
                  onClick={() => generateWalkList(selectedArea)}
                  disabled={!!generating}
                  className="mt-2 w-full text-xs bg-indigo-600 text-white rounded px-2 py-1.5 hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-1"
                ><Footprints className="w-3 h-3" /> Generate walk list (every house)</button>
                <button
                  onClick={async () => {
                    const area = selectedArea;
                    setReportArea(area);
                    setReportData(null);
                    setReportError(null);
                    setReportLoading(true);
                    try {
                      const profile = await areaIntelligenceService.getAreaProfile(area.paths);
                      setReportData(profile);
                    } catch (e: any) {
                      setReportError(e?.message || 'Failed to build area report.');
                    } finally {
                      setReportLoading(false);
                    }
                  }}
                  className="mt-2 w-full text-xs bg-emerald-600 text-white rounded px-2 py-1.5 hover:bg-emerald-700 flex items-center justify-center gap-1"
                ><BarChart3 className="w-3 h-3" /> Area report (Census)</button>
                <button
                  onClick={async () => { await mapToolsService.deleteArea(selectedArea.id); setSelectedAreaId(null); load(); }}
                  className="mt-2 text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                ><Trash2 className="w-3 h-3" /> Delete area</button>
              </div>
            </InfoWindow>
          )}

          {/* Pin info window */}
          {selectedPin && (
            <InfoWindow position={selectedPin.location} onCloseClick={() => setSelectedPinId(null)}>
              <div className="max-w-[220px]">
                <div className="font-semibold text-gray-900">{selectedPin.name || pinCategoryMeta(selectedPin.category).label}</div>
                <div className="text-xs" style={{ color: pinCategoryMeta(selectedPin.category).color }}>{pinCategoryMeta(selectedPin.category).label}</div>
                {selectedPin.notes && <div className="text-sm text-gray-600 mt-1">{selectedPin.notes}</div>}
                <button
                  onClick={async () => { await mapToolsService.deletePin(selectedPin.id); setSelectedPinId(null); load(); }}
                  className="mt-2 text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                ><Trash2 className="w-3 h-3" /> Delete pin</button>
              </div>
            </InfoWindow>
          )}
        </BaseMap>

        {/* Save form for a pending area / pin */}
        {(pendingPaths || pendingPin) && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-xl border p-4 w-80 z-10">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">{pendingPaths ? 'Save area' : `Save ${pinCategoryMeta(pinCategory).label}`}</h3>
              <button onClick={() => { setPendingPaths(null); setPendingPin(null); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <input
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={pendingPaths ? 'Area name *' : 'Pin name (optional)'}
              className="w-full px-3 py-2 border rounded-md text-sm mb-2"
            />
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Notes (optional)"
              rows={2}
              className="w-full px-3 py-2 border rounded-md text-sm mb-3"
            />
            <button
              onClick={pendingPaths ? saveArea : savePin}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
            >
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        )}

        {/* Area intelligence report (Census/ACS) */}
        {reportArea && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setReportArea(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-600" /> Area report — {reportArea.name}</h3>
                <button onClick={() => setReportArea(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4">
                {reportLoading && (
                  <div className="py-8 text-center text-gray-600"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-600" />Pulling Census data…</div>
                )}
                {reportError && !reportLoading && (
                  <p className="text-sm text-red-600 py-4">{reportError}</p>
                )}
                {reportData && !reportLoading && (
                  <>
                    <p className="text-xs text-gray-500 mb-3">{reportData.tractName}</p>
                    <dl className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Population', value: reportData.population?.toLocaleString() },
                        { label: 'Households', value: reportData.households?.toLocaleString() },
                        { label: 'Median age', value: reportData.medianAge != null ? `${reportData.medianAge}` : null },
                        { label: 'Median household income', value: reportData.medianHouseholdIncome != null ? `$${reportData.medianHouseholdIncome.toLocaleString()}` : null },
                        { label: 'Median home value', value: reportData.medianHomeValue != null ? `$${reportData.medianHomeValue.toLocaleString()}` : null },
                        { label: 'Median gross rent', value: reportData.medianGrossRent != null ? `$${reportData.medianGrossRent.toLocaleString()}/mo` : null },
                      ].map((m) => (
                        <div key={m.label} className="bg-gray-50 rounded-lg p-3">
                          <dt className="text-xs text-gray-500">{m.label}</dt>
                          <dd className="text-lg font-semibold text-gray-900">{m.value ?? '—'}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="text-[11px] text-gray-400 mt-3">Source: U.S. Census ACS {reportData.acsYear} 5-year estimates. {reportData.note}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MapsPage: React.FC = () => (
  <GoogleMapProvider>
    <MapsInner />
  </GoogleMapProvider>
);

export default MapsPage;
